import { h, type Component, type VNodeChild } from 'vue';
import type { FormatterSpec, GridFormatter } from '../types';
import { resolveFormatter } from './registry';

export type ChainContextSource = 'row' | 'item' | 'value';

export interface ChainOptions {
  formatters?: FormatterSpec[];
  separator?: string;
  formatterContext?: ChainContextSource;
}

function asText(value: any): string {
  return value == null ? '' : String(value);
}

function flattenOptions(spec: FormatterSpec): FormatterSpec {
  if (
    spec &&
    typeof spec === 'object' &&
    !Array.isArray(spec) &&
    typeof (spec as { name?: unknown }).name === 'string' &&
    (spec as { opts?: unknown }).opts
  ) {
    const { opts, ...rest } = spec as { opts: Record<string, any> } & Record<
      string,
      any
    >;
    return { ...rest, ...opts } as FormatterSpec;
  }
  return spec;
}

function nestedRenderer(componentStages: GridFormatter[]): Component {
  const renderer = (props: { value: any; context: any }): VNodeChild =>
    componentStages.reduce<VNodeChild>(
      (child, stage) =>
        h(
          stage.component!,
          { value: props.value, ...stage.props?.(props.value, props.context) },
          () => [child],
        ),
      props.value,
    );
  renderer.props = ['value', 'context'];
  return renderer as Component;
}

function mergeCellClasses(
  results: (string | Record<string, boolean>)[],
): Record<string, boolean> {
  const merged: Record<string, boolean> = {};
  for (const result of results) {
    if (typeof result === 'string') {
      for (const name of result.split(/\s+/)) {
        if (name) {
          merged[name] = true;
        }
      }
    } else if (result) {
      Object.assign(merged, result);
    }
  }
  return merged;
}

export function chain(options: ChainOptions = {}): GridFormatter {
  const stages = (options.formatters ?? [])
    .map(flattenOptions)
    .map((spec) => resolveFormatter(spec))
    .filter((stage): stage is GridFormatter => stage != null);

  const separator = options.separator ?? ' ';
  const rowIsContext = (options.formatterContext ?? 'row') === 'row';
  const componentStages = stages.filter((stage) => stage.component);
  const firstComponentIndex = stages.findIndex((stage) => stage.component);

  function contextFor(value: any, row: any): any {
    return rowIsContext ? row : value;
  }

  function reduceText(applied: GridFormatter[], value: any, context: any): any {
    return applied.reduce(
      (current, stage) =>
        stage.toText ? stage.toText(current, context) : current,
      value,
    );
  }

  function evaluate(
    value: any,
    row: any,
    run: (input: any, context: any) => any,
  ): any {
    if (Array.isArray(value)) {
      return value
        .map((item) => asText(run(item, rowIsContext ? row : item)))
        .join(separator);
    }
    return run(value, contextFor(value, row));
  }

  const formatter: GridFormatter = {
    toText: (value, row) =>
      asText(
        evaluate(value, row, (input, context) =>
          reduceText(stages, input, context),
        ),
      ),
  };

  if (stages.some((stage) => stage.toValue)) {
    formatter.toValue = (value, row) => {
      if (Array.isArray(value)) {
        return formatter.toText!(value, row);
      }
      const context = contextFor(value, row);
      let current = value;
      let typed: string | number | Date | null = null;
      for (const stage of stages) {
        if (stage.toValue) {
          typed = stage.toValue(current, context);
        }
        if (stage.toText) {
          current = stage.toText(current, context);
        }
      }
      return typed;
    };
  }

  if (componentStages.length) {
    formatter.collection = { separator };
    const head = stages.slice(0, firstComponentIndex);
    const displayFor = (value: any, row: any) =>
      evaluate(value, row, (input, context) =>
        reduceText(head, input, context),
      );

    if (componentStages.length === 1) {
      const only = componentStages[0];
      formatter.component = only.component;
      formatter.props = (value, row) => {
        const display = displayFor(value, row);
        return {
          value: display,
          ...only.props?.(display, contextFor(value, row)),
        };
      };
    } else {
      formatter.component = nestedRenderer(componentStages);
      formatter.props = (value, row) => ({
        value: displayFor(value, row),
        context: contextFor(value, row),
      });
    }
  }

  const classStages = stages.filter((stage) => stage.cellClass);
  if (classStages.length) {
    formatter.cellClass = (value, row) => {
      const context = contextFor(value, row);
      return mergeCellClasses(
        classStages.map((stage) => stage.cellClass!(value, context)),
      );
    };
  }

  if (stages.some((stage) => stage.command)) {
    formatter.command = true;
  }

  return formatter;
}
